import { IconEdit } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDropdownOptions } from '@/contexts/dropdown-options-context';
import { formatErrorMessage } from '@/utils/error-formatting';
import {
  type ApiRowType,
  type EntityConfig,
  type FieldConfig,
  referenceAdminEntityName,
} from './reference-admin-types';

interface EditReferenceAdminModalProps {
  config: EntityConfig;
  entity: ApiRowType;
  onSuccess?: (() => void) | undefined;
}

export const EditReferenceAdminModal: React.FC<EditReferenceAdminModalProps> = ({
  config,
  entity,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currencyOptions, countryOptions, refetch: refetchDropdowns } = useDropdownOptions();

  // Initialize form data with entity values
  const initialFormData = config.fields.reduce(
    (acc, field) => {
      let entityValue = entity[field.key];

      // Handle special mappings for nested data
      if (field.key === 'currency_code' && !entityValue && entity.currency) {
        entityValue = (entity.currency as { code: string }).code;
      }
      if (field.key === 'country_code' && !entityValue && entity.country) {
        entityValue = (entity.country as { code: string }).code;
      }

      acc[field.key] =
        entityValue ??
        field.defaultValue ??
        (field.type === 'checkbox' ? false : field.type === 'number' ? 0 : '');
      return acc;
    },
    {} as Record<string, unknown>
  );

  const [formData, setFormData] = useState(initialFormData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Transform data before sending
      const transformedData = { ...formData };

      // Convert string fields to uppercase where needed (code fields)
      config.fields.forEach((field) => {
        if (field.key.includes('code') && typeof transformedData[field.key] === 'string') {
          transformedData[field.key] = (transformedData[field.key] as string).toUpperCase();
        }
        // Convert empty strings to null for optional fields
        if (!field.required && transformedData[field.key] === '') {
          transformedData[field.key] = null;
        }
      });

      await config.updateFn(parseInt(entity.id), transformedData);

      // Refetch dropdown options to include any updated values
      refetchDropdowns();

      setOpen(false);
      onSuccess?.();
    } catch (_error) {
      const errorMessage = formatErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const updateFormData = (key: string, value: unknown) => {
    setFormData((prev: Record<string, unknown>) => ({ ...prev, [key]: value }));
  };

  const handleCancel = () => {
    setFormData(initialFormData);
    setError(null);
    setOpen(false);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      // Reset form data when modal is closed
      setFormData(initialFormData);
      setError(null);
    }
    setOpen(isOpen);
  };

  const renderField = (field: FieldConfig) => {
    const value = formData[field.key];

    switch (field.type) {
      case 'checkbox':
        return (
          <Checkbox
            id={field.key}
            checked={!!value}
            onCheckedChange={(checked) => updateFormData(field.key, !!checked)}
          />
        );

      case 'select': {
        // Get dynamic options based on field key
        let options = field.options || [];
        if (field.key === 'currency_code') {
          options = currencyOptions;
        } else if (field.key === 'country_code') {
          options = countryOptions;
        }

        return (
          <Select
            value={value?.toString() || ''}
            onValueChange={(newValue) => updateFormData(field.key, newValue)}
          >
            <SelectTrigger className="col-span-3">
              <SelectValue placeholder={field.placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }

      case 'number':
        return (
          <Input
            id={field.key}
            type="number"
            value={value as string | number}
            onChange={(e) => updateFormData(field.key, parseInt(e.target.value) || 0)}
            className="col-span-3"
            placeholder={field.placeholder}
            min={field.min}
            max={field.max}
            required={field.required}
          />
        );

      default: // text
        return (
          <Input
            id={field.key}
            value={value as string | number}
            onChange={(e) => updateFormData(field.key, e.target.value)}
            className="col-span-3"
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            minLength={field.minLength}
            required={field.required}
          />
        );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title={`Edit ${config.singularName.toLowerCase()}`}
          aria-label={`Edit ${config.singularName.toLowerCase()} ${referenceAdminEntityName(entity)}`}
        >
          <IconEdit className="h-4 w-4" aria-hidden />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Edit {config.singularName}</DialogTitle>
          <DialogDescription>
            Update the {config.singularName.toLowerCase()} information. Fields marked with * are
            required.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          <div className="grid gap-4 py-4">
            {config.fields.map((field) => (
              <div key={field.key} className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor={field.key} className="text-right">
                  {field.label} {field.required && '*'}
                </Label>
                {field.type === 'checkbox' ? (
                  renderField(field)
                ) : (
                  <div className="col-span-3">{renderField(field)}</div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Updating...' : `Update ${config.singularName}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
